import { AdminPermissions } from '@zspace/roles';
import {
  Criteria,
  DeferredResponse,
  PaginatedAPIResponse,
  SalesOrder,
  SortDirection,
} from '@zspace/types';
import { Suspense, useCallback, useMemo, useState } from 'react';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
} from 'react-router-dom';
import { AdminProvider } from '../../shared/context/admin-context';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import useHttpRequest from '../../shared/hooks/http-request';
import ProtectedPage from '../../shared/protected-page/protected-page';
import BoxLayout from '../../ui/box-layout/box-layout';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import AdminHeader from '../admin-header/admin-header';
import AdminInboxTable, {
  AdminToolsView,
} from '../admin-inbox-table/admin-inbox-table';
import { fetchDevicesSalesOrders as fetchDevicesSalesOrdersRequest } from '../admin-service';
import { adminDevicesTableColumns } from '../admin-table/admin-table-columns';
const initialCriteria: Criteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: '',
  sortDirection: SortDirection.ASC,
};

const FETCH_ADMIN_DEVICES_SALES_ORDERS_ERROR_MESSAGE =
  'Devices sales orders could not be fetched. Please try again';
const FETCH_ADMIN_DEVICES_BY_SALES_ORDER_ERROR_MESSAGE =
  'Devices could not be fetched. Please try again';
const DEFAULT_INBOX_TABLE_TEXT =
  'Please select a sales order to display devices';
const TABLE_EMPTY_TEXT = 'There are no devices at the moment';
const INBOX_EMPTY_TEXT = 'There are no sales orders at the moment';

export const loader: LoaderFunction = () => {
  const response = fetchDevicesSalesOrdersRequest(initialCriteria);

  return defer({ response });
};

export function AdminDevicesPageContent() {
  const devicesSalesOrdersInitialResponse =
    useAsyncValue() as PaginatedAPIResponse<SalesOrder>;
  const [devicesSalesOrders, setDevicesSalesOrders] = useState<SalesOrder[]>(
    devicesSalesOrdersInitialResponse.data
  );
  const [devicesSalesOrdersDataResponse, setDevicesSalesOrdersDataResponse] =
    useState<PaginatedAPIResponse<SalesOrder>>(
      devicesSalesOrdersInitialResponse
    );
  const [salesOrderCriteria, setSalesOrderCriteria] =
    useState<Criteria>(initialCriteria);
  const {
    executeHttpRequest: executeFetchDevicesSalesOrdersRequest,
    isLoading: loadingInitialSalesOrders,
  } = useHttpRequest();
  const { executeHttpRequest: executeFetchMoreDevicesSalesOrdersRequest } =
    useHttpRequest();

  const hasMoreDevicesSalesOrders = useMemo(
    () => !!devicesSalesOrdersDataResponse.hasMore,
    [devicesSalesOrdersDataResponse]
  );

  const fetchDeviceSalesOrders = useCallback(
    async (criteria: Criteria) =>
      executeFetchDevicesSalesOrdersRequest({
        asyncFunction: async () => {
          const response = await fetchDevicesSalesOrdersRequest(criteria);
          setDevicesSalesOrders(response.data);
          setDevicesSalesOrdersDataResponse(response);
        },
        customErrorMessage: FETCH_ADMIN_DEVICES_SALES_ORDERS_ERROR_MESSAGE,
      }),
    [executeFetchDevicesSalesOrdersRequest]
  );

  const fetchMoreDevicesSalesOrders = useCallback(
    () =>
      executeFetchMoreDevicesSalesOrdersRequest({
        asyncFunction: async () => {
          const nextPage = salesOrderCriteria.pageNumber + 1;
          const updatedCriteria = {
            ...salesOrderCriteria,
            pageNumber: nextPage,
          };
          const response = await fetchDevicesSalesOrdersRequest(
            updatedCriteria
          );
          setSalesOrderCriteria(updatedCriteria);
          setDevicesSalesOrders((prevDevicesSalesOrders) =>
            prevDevicesSalesOrders.concat(response.data)
          );
          setDevicesSalesOrdersDataResponse(response);
        },
        customErrorMessage: FETCH_ADMIN_DEVICES_SALES_ORDERS_ERROR_MESSAGE,
      }),
    [salesOrderCriteria, executeFetchMoreDevicesSalesOrdersRequest]
  );

  const handleTableFilterSubmit = useCallback(
    async (updatedDevicesSalesOrdercriteria: Criteria) => {
      await fetchDeviceSalesOrders(updatedDevicesSalesOrdercriteria);
    },
    [fetchDeviceSalesOrders]
  );

  const onSearchChange = useCallback(
    (search: string) => {
      const updatedCriteria = {
        ...initialCriteria,
        search,
      };
      setSalesOrderCriteria(updatedCriteria);
      handleTableFilterSubmit(updatedCriteria);
    },
    [handleTableFilterSubmit]
  );

  return (
    <BoxLayout
      className="is-min-height-80 mx-10 my-4 mb-4"
      header={
        <AdminHeader
          title="Devices"
          search={salesOrderCriteria.search}
          onSearchChange={onSearchChange}
        />
      }
    >
      <AdminInboxTable
        fetchMoreInboxItems={fetchMoreDevicesSalesOrders}
        hasMoreInboxItems={hasMoreDevicesSalesOrders}
        inboxItems={devicesSalesOrders}
        loadingInitialInboxItems={loadingInitialSalesOrders}
        tableColumns={adminDevicesTableColumns}
        searchValue={salesOrderCriteria.search}
      />
    </BoxLayout>
  );
}

export function AdminDevicesPage() {
  const { response } = useLoaderData() as DeferredResponse<
    Promise<PaginatedAPIResponse<SalesOrder>>
  >;

  return (
    <ProtectedPage permissions={AdminPermissions.ADMIN_TOOLS_ACCESS}>
      <Suspense fallback={<PageSpinner />}>
        <ErrorHandlingAwait resolve={response}>
          <AdminProvider
            adminView={AdminToolsView.DEVICES}
            fetchTableRecordsErrorMessage={
              FETCH_ADMIN_DEVICES_BY_SALES_ORDER_ERROR_MESSAGE
            }
            defaultInboxTableText={DEFAULT_INBOX_TABLE_TEXT}
            tableEmptyText={TABLE_EMPTY_TEXT}
            inboxEmptyText={INBOX_EMPTY_TEXT}
          >
            <AdminDevicesPageContent />
          </AdminProvider>
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default AdminDevicesPage;
